/**
 * @file Live tuning layer over the player build (issue #49).
 *
 * {@link ./GameConfig.js CONFIG}`.player` is frozen on purpose: it is the feel
 * the build ships with. This module is the editable copy of it — a flat map of
 * overrides keyed by dotted path, persisted in `localStorage`, that the debug
 * window writes to and the player systems read through. With no overrides
 * stored, the merged view equals `CONFIG.player` value for value, so a normal
 * player is never affected.
 *
 * Adding a knob costs nothing here: every numeric field of `CONFIG.player`,
 * nested ones included (`trail.distance`), appears in the merged view and in
 * {@link inspectPlayerTuning} automatically. Colour fields are the one thing
 * left out — a packed `0xRRGGBB` int is not a balance knob, so anything named
 * `*color` passes straight through and can never be overridden.
 *
 * It is the sibling of {@link ./WaveTuning.js}; the two share a shape so the
 * `ñ` sandbox can drive both through one set of habits.
 */

import { CONFIG } from './GameConfig.js';
import { humanise } from '../utils/Text.js';

/** `localStorage` key holding the override map. */
export const PLAYER_TUNING_STORAGE_KEY = 'spin.playerTuning.v1';

/**
 * Per-field editing rules, keyed by full dotted path so nested `trail` fields
 * get their own labels. A field missing from the table still shows up — it just
 * falls back to {@link DEFAULT_META} — so the panel never has to be updated in
 * lockstep with `CONFIG.player`.
 * @type {Readonly<Record<string, { label: string, step: number, min: number, integer?: boolean }>>}
 */
const PLAYER_META = Object.freeze({
  radius: { label: 'Radius', step: 0.01, min: 0.1 },
  height: { label: 'Height', step: 0.1, min: 0.1 },
  killTimerMax: { label: 'Kill timer (s)', step: 1, min: 1, integer: true },
  killTimerDamagePenalty: { label: 'Damage penalty (s/hp)', step: 0.01, min: 0 },
  speed: { label: 'Speed', step: 0.5, min: 0.5 },
  acceleration: { label: 'Acceleration', step: 5, min: 1 },
  deceleration: { label: 'Deceleration', step: 5, min: 1 },
  invulnerabilityTime: { label: 'I-frames', step: 0.05, min: 0 },
  footstepInterval: { label: 'Footstep interval', step: 0.01, min: 0.02 },
  aimHeight: { label: 'Aim height', step: 0.05, min: 0 },
  'trail.distance': { label: 'Trail distance', step: 0.05, min: 0.05 },
  'trail.size': { label: 'Trail size', step: 0.02, min: 0.01 },
  'trail.life': { label: 'Trail life', step: 0.02, min: 0.01 },
  'damageFeedback.shake': { label: 'Damage shake', step: 0.01, min: 0 },
  'damageFeedback.flashDuration': { label: 'Damage flash (s)', step: 0.01, min: 0 },
  'dash.distance': { label: 'Dash distance', step: 0.25, min: 0.5 },
  'dash.duration': { label: 'Dash duration (s)', step: 0.01, min: 0.02 },
  'dash.cooldown': { label: 'Dash cooldown (s)', step: 0.05, min: 0 },
  'dash.radius': { label: 'Dash hit radius', step: 0.05, min: 0.1 },
  'dash.invulnerability': { label: 'Dash i-frames (s)', step: 0.01, min: 0 },
  'dash.shake': { label: 'Dash shake', step: 0.01, min: 0 }
});

const DEFAULT_META = Object.freeze({ label: '', step: 0.1, min: 0 });

/**
 * Overrides only — never a full copy of `CONFIG.player`. Keeping it sparse is
 * what makes "reset" a delete and what keeps saved builds readable.
 * @type {Record<string, number>}
 */
let overrides = {};

/** Memoised merge of `CONFIG.player` + {@link overrides}. @type {object | null} */
let merged = null;

/** @type {Set<(tuning: object) => void>} */
const listeners = new Set();

/**
 * Storage backend. Swappable so tests — and any future sandbox harness — can
 * run the tuning layer without a DOM.
 * @type {Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null}
 */
let storage = detectStorage();

/**
 * @returns {Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null} The
 *   browser store, or null where there is none (tests, SSR, blocked cookies).
 */
function detectStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Safari throws instead of returning null when storage is blocked.
    return null;
  }
}

/**
 * @param {string} key Object key, the last segment of a path.
 * @returns {boolean} True for palette references, which are not balance knobs.
 */
function isColorKey(key) {
  return /color/i.test(key);
}

/**
 * Replaces the persistence backend.
 * @param {Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null} next Store to use.
 * @returns {void}
 */
export function setPlayerTuningStorage(next) {
  storage = next;
}

/**
 * Merged, read-only view of the player build. This is what the player systems
 * read. Its shape mirrors `CONFIG.player` exactly — nested objects included —
 * so a consumer reads `getPlayerTuning().speed` or `getPlayerTuning().trail.life`
 * just as it would the raw config.
 *
 * @returns {typeof CONFIG.player} `CONFIG.player` with the current overrides applied.
 */
export function getPlayerTuning() {
  if (!merged) merged = buildMerged('', CONFIG.player);
  return /** @type {typeof CONFIG.player} */ (merged);
}

/**
 * @param {string} prefix Dotted path of `node` within `CONFIG.player`.
 * @param {Record<string, any>} node Sub-tree of the config being merged.
 * @returns {object} A frozen merge of `node` and the matching {@link overrides}.
 */
function buildMerged(prefix, node) {
  /** @type {Record<string, any>} */
  const result = {};
  for (const [key, value] of Object.entries(node)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = buildMerged(path, value);
    } else if (typeof value === 'number' && !isColorKey(key)) {
      result[key] = overrides[path] ?? value;
    } else {
      // Colours, strings, booleans: not tunable, passed through untouched.
      result[key] = value;
    }
  }
  return Object.freeze(result);
}

/** @returns {void} Drops the memoised merge and notifies subscribers. */
function invalidate() {
  merged = null;
  const tuning = getPlayerTuning();
  for (const listener of listeners) listener(tuning);
}

/**
 * @param {Record<string, any>} root Tree to read from.
 * @param {string} path Dotted path.
 * @returns {any} The value at `path`, or undefined if any segment is missing.
 */
function readPath(root, path) {
  return path.split('.').reduce((node, seg) => (node == null ? undefined : node[seg]), root);
}

/**
 * Every overridable field, deepest-last, as dotted paths. Derived from
 * `CONFIG.player` so a new numeric field becomes tunable by existing, not by an
 * edit here. Colour leaves are skipped.
 *
 * @returns {string[]} e.g. `['radius', 'speed', ..., 'trail.distance']`.
 */
export function playerTuningPaths() {
  /** @type {string[]} */
  const paths = [];
  const walk = (prefix, node) => {
    for (const [key, value] of Object.entries(node)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) walk(path, value);
      else if (typeof value === 'number' && !isColorKey(key)) paths.push(path);
    }
  };
  walk('', CONFIG.player);
  return paths;
}

/**
 * @param {string} path Field path.
 * @returns {{ label: string, step: number, min: number, integer?: boolean }} Editing rules.
 */
export function getPlayerFieldMeta(path) {
  return PLAYER_META[path] ?? { ...DEFAULT_META, label: humanise(path.split('.').pop() ?? path) };
}

/**
 * @param {string} path Field path.
 * @returns {number | undefined} The value shipped in the code, ignoring overrides.
 */
export function getPlayerDefaultValue(path) {
  const value = readPath(CONFIG.player, path);
  const key = path.split('.').pop() ?? path;
  return typeof value === 'number' && !isColorKey(key) ? value : undefined;
}

/**
 * Applies one edit. Takes effect on the next read by a player system — for the
 * live-wired knobs (speed, footsteps, aim, acceleration, i-frames) that
 * means the very next frame, no reload. Fields baked into components at spawn
 * (max health, radius, trail) take effect on the next player spawn.
 *
 * @param {string} path Field path.
 * @param {number | string} rawValue New value; strings come straight from an input.
 * @returns {boolean} False when the path is unknown or the value is not a finite number.
 */
export function setPlayerValue(path, rawValue) {
  const fallback = getPlayerDefaultValue(path);
  if (fallback === undefined) return false;

  const parsed = typeof rawValue === 'number' ? rawValue : Number.parseFloat(String(rawValue));
  if (!Number.isFinite(parsed)) return false;

  const meta = getPlayerFieldMeta(path);
  let value = Math.max(meta.min, parsed);
  if (meta.integer) value = Math.round(value);

  // Storing a value equal to the default would persist noise and make a reset
  // to code defaults indistinguishable from an explicit edit.
  if (value === fallback) delete overrides[path];
  else overrides[path] = value;

  invalidate();
  return true;
}

/** @returns {Record<string, number>} A copy of the current override map. */
export function getPlayerOverrides() {
  return { ...overrides };
}

/** @returns {boolean} True when anything differs from the shipped build. */
export function hasPlayerOverrides() {
  return Object.keys(overrides).length > 0;
}

/**
 * @param {string} path Field path.
 * @returns {boolean} True when this field has been edited away from the code default.
 */
export function isPlayerValueOverridden(path) {
  return path in overrides;
}

/**
 * Replaces every override at once. Unknown or invalid entries are skipped, so a
 * stale saved payload can never break a build that renamed a field.
 *
 * @param {Record<string, number>} next Override map.
 * @returns {number} How many entries were applied.
 */
export function applyPlayerOverrides(next) {
  overrides = {};
  let applied = 0;

  for (const [path, value] of Object.entries(next ?? {})) {
    const fallback = getPlayerDefaultValue(path);
    if (fallback === undefined || typeof value !== 'number' || !Number.isFinite(value)) continue;
    const meta = getPlayerFieldMeta(path);
    const clamped = meta.integer ? Math.round(Math.max(meta.min, value)) : Math.max(meta.min, value);
    if (clamped === fallback) continue;
    overrides[path] = clamped;
    applied++;
  }

  invalidate();
  return applied;
}

/**
 * Persists the overrides so they survive a page reload.
 * @returns {boolean} False when there is no writable store.
 */
export function savePlayerTuning() {
  if (!storage) return false;
  try {
    storage.setItem(PLAYER_TUNING_STORAGE_KEY, JSON.stringify(overrides));
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads persisted overrides. Called once at boot, before the player spawns.
 * @returns {number} How many overrides were restored.
 */
export function loadPlayerTuning() {
  if (!storage) return 0;
  try {
    const raw = storage.getItem(PLAYER_TUNING_STORAGE_KEY);
    if (!raw) return 0;
    return applyPlayerOverrides(JSON.parse(raw));
  } catch {
    // A corrupt payload must never keep the game from booting.
    return 0;
  }
}

/**
 * Discards every override, in memory and on disk, restoring the code defaults.
 * @returns {void}
 */
export function resetPlayerTuning() {
  overrides = {};
  try {
    storage?.removeItem(PLAYER_TUNING_STORAGE_KEY);
  } catch {
    // Nothing to do: the in-memory reset already happened.
  }
  invalidate();
}

/**
 * The current build as pasteable JSON, shaped like the `CONFIG.player` block it
 * would replace, so tuned numbers can go straight back into the source config.
 *
 * @returns {string} Pretty-printed JSON.
 */
export function exportPlayerTuning() {
  return JSON.stringify({ overrides: getPlayerOverrides(), player: getPlayerTuning() }, null, 2);
}

/**
 * Subscribes to edits.
 * @param {(tuning: object) => void} listener Called after every change.
 * @returns {() => void} Unsubscribe.
 */
export function onPlayerTuningChanged(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * A render-ready snapshot of the whole tuning surface: one row per overridable
 * field, with its live value, code default and editing rules. This is what the
 * debug window draws, and it is derived from `CONFIG.player` so new fields
 * appear with no edit to the UI.
 *
 * @returns {{
 *   scalars: Array<{ path: string, label: string, value: number, defaultValue: number, step: number, min: number, integer: boolean, overridden: boolean }>
 * }} Everything the Player section of the debug window draws.
 */
export function inspectPlayerTuning() {
  const tuning = getPlayerTuning();
  const scalars = playerTuningPaths().map((path) => {
    const meta = getPlayerFieldMeta(path);
    return {
      path,
      label: meta.label || humanise(path.split('.').pop() ?? path),
      value: /** @type {number} */ (readPath(tuning, path)),
      defaultValue: /** @type {number} */ (getPlayerDefaultValue(path)),
      step: meta.step,
      min: meta.min,
      integer: meta.integer === true,
      overridden: path in overrides
    };
  });
  return { scalars };
}
